import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import {
  PaintBucket,
  Users,
  Award,
  ShieldCheck,
  Target,
  Heart,
  ArrowRight,
} from "lucide-react";

const teamMembers = [
  {
    name: "Company Owner",
    role: "Founder & Lead Estimator",
    experience: "10+ Years",
    image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop",
    alt: "House painters in Lake Nona area owner providing free painting estimates for Orlando homes",
  },
  {
    name: "Project Manager",
    role: "Operations Manager",
    experience: "8+ Years",
    image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=400&fit=crop",
    alt: "Interior home painters in Lake Mary Florida operations manager coordinating residential projects",
  },
  {
    name: "Lead Painter",
    role: "Senior Crew Lead",
    experience: "12+ Years",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop",
    alt: "Exterior painters in Orlando Florida senior crew lead with 12 years experience",
  },
  {
    name: "Color Consultant",
    role: "Design Specialist",
    experience: "7+ Years",
    image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop",
    alt: "Interior painting companies in Winter Springs Florida color consultant helping homeowners",
  },
];

const values = [
  {
    icon: Target,
    title: "Quality First",
    description: "Premium paints and meticulous prep for lasting results",
  },
  {
    icon: ShieldCheck,
    title: "Honest Pricing",
    description: "Transparent quotes with no hidden fees or surprises",
  },
  {
    icon: Heart,
    title: "Customer Focus",
    description: "Your satisfaction drives everything we do",
  },
  {
    icon: Users,
    title: "Skilled Team",
    description: "Experienced painters who take pride in their craft",
  },
];

const certifications = [
  { name: "Orlando Born & Bred", number: "Always Active" },
  { name: "We’ve Got You Covered", number: "$1M+ Coverage" },
  { name: "Safe for Kids & Pets", number: "EPA Lead-Safe" },
  { name: "Your Neighbors Love Us", number: "A+ Rated" },
  { name: "No Mess, No Stress", number: "Google Reviews" },
  { name: "Our Word is Our Bond", number: "100% Satisfaction Guaranteed" },
];

export const AboutPage = () => {
  return (
    <main className="pt-20">
      {/* Hero Banner */}
      <div className="relative bg-brand-navy py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.pexels.com/photos/5691610/pexels-photo-5691610.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Interior painters near Orlando Florida transforming residential spaces"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/95 to-brand-navy/80" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <span className="inline-block text-sm font-medium uppercase tracking-widest text-brand-orange mb-4">
            About Us
          </span>
          <h1 className="font-heading text-5xl md:text-7xl font-bold tracking-tight uppercase text-white max-w-4xl">
            Orlando's Trusted
            <br />
            <span className="text-brand-orange">Painting Pros</span>
          </h1>
          <p className="font-body text-xl text-white/70 mt-6 max-w-2xl">
            For over 10 years, Roll Orlando has been transforming homes across 
            Central Florida with expert painting services and unmatched attention to detail.
          </p>
        </div>
      </div>

      {/* Company Story */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-sm font-medium uppercase tracking-widest text-brand-orange mb-4">
                Our Story
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-brand-navy mb-6">
                A Decade of Painting Excellence
              </h2>
              <div className="space-y-4 text-brand-slate">
                <p>
                  Roll Orlando was founded with a simple mission: to provide 
                  Central Florida homeowners with professional painting services 
                  they can trust at prices they can afford.
                </p>
                <p>
                  Over the past 10+ years, we've grown from a small local operation 
                  to one of Orlando's most trusted painting companies, completing 
                  over 1,000 residential projects across Orange, Seminole, and 
                  Osceola counties.
                </p>
                <p>
                  Our success is built on attention to detail, honest communication, 
                  and treating every home like it's our own. We use only premium 
                  paints engineered for Florida's climate, and our skilled team 
                  takes pride in delivering flawless results every time.
                </p>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-6 mt-8 pt-8 border-t border-border">
                <div>
                  <div className="font-heading text-4xl font-black text-brand-orange">10+</div>
                  <div className="text-sm text-brand-slate">Years in Business</div>
                </div>
                <div>
                  <div className="font-heading text-4xl font-black text-brand-orange">1000+</div>
                  <div className="text-sm text-brand-slate">Projects Completed</div>
                </div>
                <div>
                  <div className="font-heading text-4xl font-black text-brand-orange">15+</div>
                  <div className="text-sm text-brand-slate">Expert Painters</div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-square rounded-sm overflow-hidden">
                <img
                  src="/images/roll-orlando-storefront.jpg"
                  alt="Roll Orlando Painting Company storefront in Orlando Florida"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating accent */}
              <div className="absolute -bottom-8 -left-8 bg-brand-orange p-6 rounded-sm">
                <div className="flex items-center gap-4 text-white">
                  <PaintBucket className="w-12 h-12" />
                  <div>
                    <div className="font-heading text-2xl font-bold">Est. 2014</div>
                    <div className="text-sm">Orlando, Florida</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-brand-ice">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-medium uppercase tracking-widest text-brand-orange mb-4">
              Our Values
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-brand-navy">
              What Drives Us
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value) => {
              const Icon = value.icon;
              return (
                <div
                  key={value.title}
                  className="bg-white p-8 rounded-sm border border-border text-center hover:border-brand-orange/50 transition-colors"
                >
                  <div className="w-16 h-16 bg-brand-navy rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-8 h-8 text-brand-orange" />
                  </div>
                  <h3 className="font-heading text-xl font-bold uppercase text-brand-navy mb-2">
                    {value.title}
                  </h3>
                  <p className="text-sm text-brand-slate">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      

      {/* Certifications & Licensing */}
      <section className="py-24 bg-brand-navy">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-medium uppercase tracking-widest text-brand-orange mb-4">
              Local. Reliable. Real.
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-white">
              The Peace of Mind You Deserve
            </h2>
            <p className="font-body text-lg text-white/70 mt-4 max-w-2xl mx-auto">
              We treat your home like it’s our own. From the first drop of paint to the final walkthrough
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certifications.map((cert) => (
              <div
                key={cert.name}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-sm p-6 hover:border-brand-orange/50 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-orange/20 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-brand-orange" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-white mb-1">
                      {cert.name}
                    </h3>
                    <p className="text-sm text-brand-orange font-mono">
                      {cert.number}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-white/50 text-sm">
              Real People, Real Service: No confusing sales bots. You’ll talk to the same person from estimate to finish..
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-brand-orange">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-brand-navy mb-6">
            Ready to Transform Your Home?
          </h2>
          <p className="text-lg text-brand-navy/80 mb-8 max-w-2xl mx-auto">
            Let's discuss your painting project. Free estimates, honest pricing, and results you'll love.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/quote">
              <Button
                size="lg"
                className="bg-brand-navy text-white font-heading font-bold uppercase tracking-wide hover:bg-brand-navy/90 h-14 px-8"
              >
                Get Free Estimate
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-brand-navy text-brand-navy font-heading font-bold uppercase tracking-wide hover:bg-brand-navy hover:text-white h-14 px-8"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};
